/**
 * ContentDataSection
 * ------------------
 * WHAT: Renders a single free-form content section (title + rich-text body
 *       + optional tech-writer metadata). Used by ContentDataForm for every
 *       section whose UIComponent is `ContentDataSection`.
 *
 * WHY class (not hook): predates hooks. Small enough to migrate; the only
 *       real state is modal + expansion + edited-title draft.
 *
 * RESPONSIBILITIES:
 *   - Wrap body in an ExpandableSection in edit mode (so authors can collapse
 *     long sections) and a plain header in readonly mode.
 *   - Provide a modal to edit the section title (for custom Guide/Strategy sections).
 *   - Sync tech-writer metadata (abstract / href) up through onContentChanged.
 */
import {
  Button,
  ExpandableSection,
  FormField,
  Modal,
  SpaceBetween,
  Box,
  NonCancelableCustomEvent,
  ModalProps,
} from '@amzn/awsui-components-core';
import {
  ContentAccessUser,
  ContentHistory,
  ContentNode,
  ContentNodeType,
  ContentType,
  ContentWorkflow,
} from '@amzn/awscat-pattern-library-service-model';
import React from 'react';
import constants from '../../../common/constants';
import { ContentSchemaSection, ContentSectionKey } from '../../../model/ContentSchemaSection';
import TechwriterMetadata from '../../../model/TechwriterMetadata';
import '../../../resources/styles/scss/components/pages/content/ReadOnlyPattern.css';
import ContentSectionHeader from '../../content/ContentSectionHeader';
import TechwriterMetadataEditor from './TechwriterMetadataEditor';
import EditorInput from '../../../editor/EditorInput';
import { TiptapOutputType } from '@amzn/awscat-editor';
import { DismissModalReason } from '../../../model/Enum';
import TutorialLink from '../../content/TutorialLink';
import EditorWrapper from '../../../editor/EditorWrapper';
import { TiptapMenuType } from '../../../editor/utils';
// DEAD IMPORT: DiagramGuidelinesDeck is imported but never referenced in this file.
import DiagramGuidelinesDeck from '../../content/DiagramGuidelinesDeck';

/**
 * Props for ContentDataSection.
 *
 * Only a handful are truly required — the component's default props cover
 * the rest. Callbacks are all "notify parent" style; this component does
 * not persist anything on its own.
 */
export interface ContentDataSectionProps {
  contentId: string;
  workflowId: number;
  sectionId: string; // Stable id for this section (matches template key for built-in sections).
  header: string; // Display title, already resolved from template or override.
  content: ContentNode; // The section node (carries contentData, abstract, href, title).
  readonly: boolean;
  showTechwriterMetadata: boolean; // Show abstract/href editor.
  sectionEditable?: boolean; // Allow renaming via the modal (only custom sections).
  onContentChanged?: (
    sectionId: string,
    key: ContentSectionKey,
    newContent: TiptapOutputType,
    validationError?: any,
  ) => void;
  onSectionDeleted?: (sectionId: string) => void;
  onSectionUpdated?: (sectionId: string, attributes: { title: any; shortTitle?: any }) => void;
  expanded?: boolean; // Initial expanded state of the ExpandableSection.
  sectionSortable?: boolean; // Show drag handle.
  showSectionalComments?: boolean;
  workflow?: ContentWorkflow;
  workflowHistory?: ContentHistory[];
  coAuthors?: ContentAccessUser[];
}

interface ContentDataSectionState {
  techwriterMetadata: TechwriterMetadata; // Local copy so the editor stays responsive.
  sectionExpanded: boolean; // Controlled state of ExpandableSection.
  isEditingHeader: boolean; // Whether the rename-section modal is open.
  sectionTitle: TiptapOutputType; // Draft value while editing the title.
  sectionShortTitle: TiptapOutputType; // Draft value while editing the short title.
}

class ContentDataSection extends React.Component<ContentDataSectionProps, ContentDataSectionState> {
  // Defaults for optional props. Note the ContentNode.create fallback — this
  // keeps render() safe even when parent forgets to pass `content` (shouldn't
  // happen in practice, but guards against the legacy EditContentData paths).
  static defaultProps = {
    content: ContentNode.create('Untitled', '', ContentNodeType.Section, ''),
    expanded: true,
    readonly: false,
    sectionEditable: false,
    sectionSortable: true,
    // DEAD: showContentAbstract is not in the props interface, ignored by React.
    showContentAbstract: false,
    showSectionalComments: false,
    showTechwriterMetadata: false,
  };

  constructor(props: ContentDataSectionProps) {
    super(props);

    const { content, header } = props;
    this.state = {
      isEditingHeader: false,
      sectionExpanded: true, // Ignores `props.expanded` here — see componentDidUpdate below.
      sectionTitle: header,
      sectionShortTitle: content.shortTitle || '',
      techwriterMetadata: { abstract: content.abstract, href: content.href },
    };
  }

  /**
   * LEGACY CODE — this entire lifecycle targets the old Froala editor (CSS
   * class `rdw-image-wrapper` / `rdw-image-modal-upload-option-input`), which
   * this codebase has already migrated away from in favour of Tiptap.
   *
   * BUG / DEAD CODE: the selectors `.rdw-image-wrapper` etc. no longer exist
   * in the DOM, so `forEach` iterates an empty list and this method is a no-op.
   * Also `this.attachImageUploadAttrTimeout` is assigned inside the listener
   * but never cleared — if it ever DID work, it would leak timeouts on unmount.
   * Safe to delete when refactoring.
   */
  public componentDidMount() {
    // Adds type image to file upload control
    // Add click listener to every image button on editor toolbar
    // @ts-ignore
    [...document.getElementsByClassName('rdw-image-wrapper')].forEach((imageWrapper) => {
      // Add accept attribute to all file upload controls.
      imageWrapper.addEventListener('click', function () {
        // @ts-expect-error TS2683 - strict migration
        this.attachImageUploadAttrTimeout = setTimeout(() => {
          // @ts-ignore
          [...document.getElementsByClassName('rdw-image-modal-upload-option-input')].forEach(
            (elem) => {
              elem.removeAttribute('accept');
              elem.setAttribute('accept', 'image/*');
            },
          );

          // @ts-expect-error TS2532 - strict migration
          const inputElem = document.getElementsByName('height')[0].parentElement;
          // @ts-expect-error TS18047 - strict migration
          inputElem.setAttribute('data-tip', constants.IMAGE_UPLOAD_HEIGHT_WIDTH_TOOLTIP);
        }, 50);
      });
    });
  }

  /**
   * Keep local state in sync with props changes.
   *
   * WHY: the parent may swap in a different section (different sectionId)
   * under the same mounted component, or update fields from a remote save.
   * We copy the new values into local state so inputs become controlled by
   * the incoming props.
   *
   * BUG: if the user is mid-edit on the title or metadata and props change
   * (e.g. from a remote sync), their unsaved draft is silently overwritten.
   * A better design would be to detect divergence and prompt the user.
   */
  public componentDidUpdate(prevProps: ContentDataSectionProps) {
    if (
      prevProps.content.contentData !== this.props.content.contentData ||
      prevProps.content.abstract !== this.props.content.abstract ||
      prevProps.content.href !== this.props.content.href ||
      prevProps.content.shortTitle !== this.props.content.shortTitle ||
      prevProps.sectionId !== this.props.sectionId
    ) {
      const { content, header } = this.props;
      this.setState({
        sectionTitle: header,
        sectionShortTitle: content.shortTitle || '',
        techwriterMetadata: { abstract: content.abstract, href: content.href },
      });
    }
    if (prevProps.expanded !== this.props.expanded) {
      // Allow the parent to force expand/collapse via props (e.g., "Expand all" button).
      const { expanded } = this.props;
      this.setState({
        // @ts-expect-error TS2322 - strict migration
        sectionExpanded: expanded,
      });
    }
  }

  public render() {
    const {
      header,
      sectionId,
      readonly,
      showTechwriterMetadata,
      sectionEditable,
      sectionSortable,
      onSectionDeleted,
      onContentChanged,
      contentId,
      content,
      workflow,
    } = this.props;
    const { techwriterMetadata, sectionExpanded, sectionTitle } = this.state;
    // The main rich-text editor for the body.
    const editor = (
      <EditorInput
        contentId={contentId}
        sectionId={sectionId}
        data={content.contentData}
        readonly={readonly}
        onContentChanged={onContentChanged}
        content={content}
      />
    );

    // Header = title + edit/delete/drag controls. Same component is used in
    // ContentDataForm for the page header.
    const sectionHeader = (
      <ContentSectionHeader
        sectionId={sectionId}
        title={header}
        // @ts-expect-error TS2322 - strict migration
        isEditable={sectionEditable}
        isSortable={sectionSortable}
        onSectionDelete={onSectionDeleted}
        onSectionEditClick={this.enableHeaderEditing}
        readonly={readonly}
        headerInfo={
          /* Tutorial link shown beside the title — opens a side panel with
             section-specific writing tips. */
          <TutorialLink
            // @ts-expect-error TS2322 - strict migration
            contentType={workflow?.draftContentMetadata?.contentType}
            schemaSection={ContentSchemaSection.ContentSections}
            sectionId={sectionId}
          />
        }
      />
    );

    // Tech-writer metadata (abstract / href) is only meaningful for Guides
    // and Strategies — Patterns auto-generate these.
    const isGuideOrStrategy = [ContentType.Guide, ContentType.Strategy].includes(
      // @ts-expect-error TS2345 - strict migration
      workflow?.draftContentMetadata?.contentType,
    );

    // 'default' variant = no border; 'container' variant = bordered panel.
    // Edit mode uses a panel so sections visually group; readonly mode is flat.
    const variant = readonly ? 'default' : 'container';

    // Two layouts. Readonly: title + body, always expanded. Edit: wrapped in
    // ExpandableSection so authors can collapse long sections while working.
    const displaySection = readonly ? (
      <div>
        {sectionHeader}
        <SpaceBetween size="xxxs">
          <TechwriterMetadataEditor
            readonly={readonly}
            visible={showTechwriterMetadata}
            metadata={techwriterMetadata}
            onInputUpdated={this.updateTechwriterMetadata}
            nodeType={content.nodeType}
            disabled={!isGuideOrStrategy}
          />
          {editor}
        </SpaceBetween>
      </div>
    ) : (
      <div>
        <ExpandableSection
          header={sectionHeader}
          variant={variant}
          onChange={this.expandSection}
          expanded={sectionExpanded}
        >
          <SpaceBetween size="xxxs">
            <TechwriterMetadataEditor
              readonly={readonly}
              visible={showTechwriterMetadata}
              metadata={techwriterMetadata}
              onInputUpdated={this.updateTechwriterMetadata}
              nodeType={content.nodeType}
              disabled={!isGuideOrStrategy}
            />
            {editor}
          </SpaceBetween>
        </ExpandableSection>
      </div>
    );
    return (
      <div>
        {displaySection}
        {/* Rename-section modal is only relevant when the title is editable
            (custom sections in Guides/Strategies). */}
        {sectionEditable && this.renderSectionEditor()}
      </div>
    );
  }

  /**
   * Render the "Edit section title" modal.
   * Symmetrical with ContentDataForm.renderPageEditModal — same errorText bug
   * (boolean instead of string) applies here too.
   */
  private renderSectionEditor = () => {
    const { isEditingHeader, sectionTitle, sectionShortTitle } = this.state;
    const formActions = (
      <Box float="right">
        <Button onClick={this.disableHeaderEditing} variant="link">
          {constants.CANCEL_BUTTON_TEXT}
        </Button>
        <Button onClick={this.editSectionTitle} variant="primary">
          {constants.SAVE_BUTTON_TEXT}
        </Button>
      </Box>
    );
    // UNLIKE the page-title modal, this editor is rendered even when the
    // modal is hidden (not guarded by isEditingHeader). Minor — the modal's
    // `visible` flag prevents it being displayed, but the editor still mounts.
    const sectionTitleEditor = (
      <EditorWrapper
        model={sectionTitle}
        readOnly={false}
        onModelChange={this.updateSectionTitle}
        menuType={TiptapMenuType.Hide}
      />
    );
    return (
      <Modal
        onDismiss={this.handleDismissModal}
        header={constants.CONTENT_SECTION_EDIT_HEADER}
        visible={isEditingHeader}
        footer={formActions}
        closeAriaLabel="Close modal"
      >
        <SpaceBetween size="l">
          <FormField
            errorText={!sectionTitle}
            label={'Section title'}
            stretch
            i18nStrings={{ errorIconAriaLabel: 'Error' }}
          >
            {sectionTitleEditor}
          </FormField>
          <FormField
            label={
              <>
                {constants.SHORT_TITLE_LABEL} - <i>optional</i>
              </>
            }
            description={constants.SHORT_TITLE_NODE_DESCRIPTION}
            stretch
          >
            <EditorWrapper
              model={sectionShortTitle}
              readOnly={false}
              onModelChange={this.updateSectionShortTitle}
              menuType={TiptapMenuType.Hide}
              removeFormatting
              singleLine
            />
          </FormField>
        </SpaceBetween>
      </Modal>
    );
  };

  /**
   * Toggle the expanded state of the ExpandableSection.
   * preventDefault stops CloudScape's default behaviour so we can fully control
   * expansion via state (some CloudScape versions still toggle internally).
   */
  // @ts-expect-error TS7006 - strict migration
  private expandSection = (e) => {
    e.preventDefault();
    this.setState({
      sectionExpanded: e.detail.expanded,
    });
  };

  /**
   * Bubble up abstract/href edits while also keeping the local copy in sync
   * so the editor remains responsive during typing (optimistic update).
   */
  private updateTechwriterMetadata = (
    sectionKey: ContentSectionKey,
    value: any,
    validationError?: string,
  ) => {
    const { techwriterMetadata } = this.state;
    this.setState({
      techwriterMetadata: {
        ...techwriterMetadata,
        [sectionKey]: value,
      },
    });
    const { sectionId, onContentChanged } = this.props;
    if (onContentChanged) {
      onContentChanged(sectionId, sectionKey, value, validationError);
    }
  };

  // Draft-title controlled-input handler (before user clicks Save).
  private updateSectionTitle = (content: TiptapOutputType) => {
    this.setState({ sectionTitle: content });
  };

  // Draft short-title controlled-input handler (before user clicks Save).
  private updateSectionShortTitle = (value: TiptapOutputType) => {
    this.setState({ sectionShortTitle: value });
  };

  /**
   * Save handler: fires onSectionUpdated({title}) and closes the modal.
   * Same no-validation issue as ContentDataForm.editPageTitle — empty title
   * will be accepted.
   */
  private editSectionTitle = () => {
    const { sectionTitle, sectionShortTitle } = this.state;
    const { sectionId, onSectionUpdated } = this.props;
    if (onSectionUpdated) {
      onSectionUpdated(sectionId, {
        title: sectionTitle,
        shortTitle: sectionShortTitle || undefined,
      });
    }
    this.setState({
      isEditingHeader: false,
    });
  };

  private enableHeaderEditing = () => {
    this.setState({
      isEditingHeader: true,
      sectionShortTitle: this.props.content.shortTitle || '',
    });
  };

  /**
   * CloudScape Modal.onDismiss fires with reasons: closeButton | overlay | keyboard.
   * We only close when the user clicked the explicit close button; clicks on the
   * overlay or ESC are IGNORED.
   *
   * WHY: prevents accidental loss of the in-progress title edit. Contrast with
   * ContentDataForm.renderPageEditModal which ignores the reason entirely.
   */
  private handleDismissModal = (
    dismissEvent: NonCancelableCustomEvent<ModalProps.DismissDetail>,
  ) => {
    const dismissReason = dismissEvent.detail.reason;
    if (dismissReason === DismissModalReason.CloseButton) {
      this.disableHeaderEditing();
    }
  };

  private disableHeaderEditing = () => {
    this.setState({
      isEditingHeader: false,
    });
  };
}

export default ContentDataSection;
